import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { GitBranch, ArrowRight, CheckCircle, Zap, Users } from 'lucide-react';
import { api } from '../utils/api';

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, register, loginWithGoogle, forgotPassword } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  // Detect invite token in URL
  useEffect(() => {
    const token = searchParams.get('invite');
    if (token) {
      api.get(`/auth/invite/${token}`).then(({ ok, data }) => {
        if (ok) {
          setInviteToken(token);
          setRegisterEmail(data.email);
          setActiveTab('register');
          setShowAuth(true);
        } else {
          toast.error(data.detail || 'Invalid or expired invitation');
        }
      });
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerEmail, registerPassword, registerName, inviteToken);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success('If an account exists, a reset link has been sent.');
      setShowForgotPassword(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <GitBranch className="w-6 h-6" />,
      title: 'Visual State Editor',
      description: 'Create and edit automata with an intuitive drag-and-drop canvas interface.',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Real-time Validation',
      description: 'Instantly check if your DFA/NFA is complete and well-formed.',
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Step-by-Step Simulation',
      description: 'Watch your automaton process input strings character by character.',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Save & Share',
      description: 'Save your automata to your account and export them anytime.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 canvas-container opacity-50" />
        
        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <GitBranch className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold font-heading">AutomataLab</span>
          </Link>
          
          <div className="flex items-center gap-3">
            <Button 
              data-testid="nav-sign-in-btn"
              onClick={() => { setActiveTab('login'); setShowAuth(true); }}
              variant="ghost"
            >
              Sign In
            </Button>
            <Button 
              data-testid="nav-register-btn"
              onClick={() => { setActiveTab('register'); setShowAuth(true); }}
            >
              Register
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 lg:py-32">
          <div className="text-center space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight animate-fade-in">
              Design Automata
              <span className="block text-gradient">Visually</span>
            </h1>
            
            <p className="max-w-2xl mx-auto text-base lg:text-lg text-muted-foreground animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Create, edit, and simulate finite state machines with an intuitive visual editor. 
              Perfect for learning automata theory or designing state-based systems.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Button 
                data-testid="hero-get-started-btn"
                onClick={() => navigate('/editor')}
                className="btn-primary text-lg px-10 py-4"
              >
                Start Creating <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            <p className="text-sm text-muted-foreground/70 animate-slide-up" style={{ animationDelay: '0.25s' }}>
              Created by <span className="font-medium text-muted-foreground">Gene Rohrbaugh</span>
            </p>
          </div>

          {/* Automaton Preview */}
          <div className="mt-16 relative animate-scale-in" style={{ animationDelay: '0.3s' }}>
            <div className="glass-panel rounded-2xl p-8 max-w-4xl mx-auto">
              <svg viewBox="0 0 600 220" className="w-full h-auto">
                {/* Subtle grid */}
                <defs>
                  <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="var(--text-primary)" strokeOpacity="0.05" strokeWidth="1" />
                  </pattern>
                </defs>
                <rect width="600" height="220" fill="url(#grid)" />

                {/* Transition q0→q1: quadratic bezier with slight upward curve */}
                <path d="M 149,117 Q 210,88 271,117" fill="none" stroke="var(--transition-stroke)" strokeWidth="2" />
                {/* Arrowhead q0→q1 */}
                <polygon points="271,117 261,112 263,122" fill="var(--transition-stroke)" />
                {/* Label "a" — edge-based: nearest edge at gap from curve */}
                <text x="210" y="78" textAnchor="middle" dominantBaseline="central" fill="var(--text-primary)" fontFamily="JetBrains Mono, monospace" fontSize="12">a</text>

                {/* Transition q1→q2: quadratic bezier with slight upward curve */}
                <path d="M 329,117 Q 390,88 451,117" fill="none" stroke="var(--transition-stroke)" strokeWidth="2" />
                {/* Arrowhead q1→q2 */}
                <polygon points="451,117 441,112 443,122" fill="var(--transition-stroke)" />
                {/* Label "b" */}
                <text x="390" y="78" textAnchor="middle" dominantBaseline="central" fill="var(--text-primary)" fontFamily="JetBrains Mono, monospace" fontSize="12">b</text>

                {/* Self-loop on q1 (upward ellipse, matching canvas: rx=20 ry=30) */}
                <ellipse cx="300" cy="65" rx="20" ry="30" fill="none" stroke="var(--transition-stroke)" strokeWidth="2" />
                {/* Self-loop arrowhead (at bottom-right where loop re-enters state) */}
                <polygon points="309,92 312,83 317,91" fill="var(--transition-stroke)" />
                {/* Label "a, b" — above the loop, edge-based */}
                <text x="300" y="19" textAnchor="middle" dominantBaseline="central" fill="var(--text-primary)" fontFamily="JetBrains Mono, monospace" fontSize="12">a, b</text>

                {/* Initial arrow for q0 */}
                <line x1="52" y1="120" x2="86" y2="120" stroke="var(--state-stroke)" strokeWidth="2" />
                <polygon points="85,115 90,120 85,125" fill="var(--state-stroke)" />

                {/* State q0 (initial) */}
                <circle cx="120" cy="120" r="30" fill="var(--state-fill)" stroke="var(--state-stroke)" strokeWidth="3" />
                <text x="120" y="120" textAnchor="middle" dominantBaseline="central" fill="var(--text-primary)" fontFamily="JetBrains Mono, monospace" fontSize="14">q0</text>

                {/* State q1 */}
                <circle cx="300" cy="120" r="30" fill="var(--state-fill)" stroke="var(--state-stroke)" strokeWidth="3" />
                <text x="300" y="120" textAnchor="middle" dominantBaseline="central" fill="var(--text-primary)" fontFamily="JetBrains Mono, monospace" fontSize="14">q1</text>

                {/* State q2 (final — double circle, green stroke) */}
                <circle cx="480" cy="120" r="30" fill="var(--state-final-fill)" stroke="var(--state-final-stroke)" strokeWidth="3" />
                <circle cx="480" cy="120" r="24" fill="none" stroke="var(--state-final-stroke)" strokeWidth="2" />
                <text x="480" y="120" textAnchor="middle" dominantBaseline="central" fill="var(--text-primary)" fontFamily="JetBrains Mono, monospace" fontSize="14">q2</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 lg:px-12 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-black text-center mb-12">
            Everything you need to master automata
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-glass p-6 space-y-4"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  {feature.icon}
                </div>
                <h3 className="font-bold text-lg">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      {showAuth && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAuth(false)}
        >
          <Card className="w-full max-w-md animate-scale-in" data-testid="auth-modal">
            <CardHeader className="text-center">
              <div className="w-12 h-12 rounded-lg bg-primary mx-auto mb-4 flex items-center justify-center">
                <GitBranch className="w-7 h-7 text-primary-foreground" />
              </div>
              <CardTitle className="text-2xl">
                {inviteToken ? "You're Invited" : 'Welcome to AutomataLab'}
              </CardTitle>
              <CardDescription>
                {showForgotPassword 
                  ? 'Enter your email to reset your password'
                  : inviteToken
                    ? 'Create your admin account to get started'
                    : 'Sign in to save and manage your automata'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      data-testid="forgot-email-input"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <Button 
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setShowForgotPassword(false)}
                  >
                    Back to login
                  </Button>
                </form>
              ) : (
                <>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                      <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                      <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                    </TabsList>

                    <TabsContent value="login">
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="login-email">Email</Label>
                          <Input
                            id="login-email"
                            type="email"
                            data-testid="login-email-input"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="login-password">Password</Label>
                          <Input
                            id="login-password"
                            type="password"
                            data-testid="login-password-input"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                          />
                        </div>
                        <button 
                          type="button"
                          className="text-sm text-primary hover:underline"
                          onClick={() => setShowForgotPassword(true)}
                          data-testid="forgot-password-link"
                        >
                          Forgot password?
                        </button>
                        <Button 
                          type="submit" 
                          className="w-full btn-primary"
                          disabled={loading}
                          data-testid="login-submit-btn"
                        >
                          {loading ? 'Signing in...' : 'Sign In'}
                        </Button>
                      </form>
                    </TabsContent>

                    <TabsContent value="register">
                      <form onSubmit={handleRegister} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="register-name">Name</Label>
                          <Input
                            id="register-name"
                            type="text"
                            data-testid="register-name-input"
                            value={registerName}
                            onChange={(e) => setRegisterName(e.target.value)}
                            placeholder="Your name"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="register-email">Email</Label>
                          <Input
                            id="register-email"
                            type="email"
                            data-testid="register-email-input"
                            value={registerEmail}
                            onChange={(e) => setRegisterEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            readOnly={!!inviteToken}
                            className={inviteToken ? 'bg-muted' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="register-password">Password</Label>
                          <Input
                            id="register-password"
                            type="password"
                            data-testid="register-password-input"
                            value={registerPassword}
                            onChange={(e) => setRegisterPassword(e.target.value)}
                            placeholder="8+ characters"
                            required
                            minLength={8}
                            maxLength={64}
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full btn-primary"
                          disabled={loading}
                          data-testid="register-submit-btn"
                        >
                          {loading ? 'Creating account...' : 'Create Account'}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={loginWithGoogle}
                    data-testid="google-login-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-primary" />
            <span className="font-bold">AutomataLab</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 Gene Rohrbaugh. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
